import React from 'react';
import { Checkbox as MuiCheckbox, FormHelperText, Box } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * AppCheckboxComponent
 * MUI Checkbox wrapper with consistent styling and label support
 * Checkbox is only clickable on the box itself, not the label
 */
const AppCheckboxComponent = ({
  checked = false,
  onChange,
  label,
  error = false,
  helperText = '',
  disabled = false,
  color = 'primary',
  size = 'medium',
  ...props
}) => {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <MuiCheckbox
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          color={color === 'primary' ? 'primary' : color === 'secondary' ? 'secondary' : color === 'error' ? 'error' : color === 'warning' ? 'warning' : color === 'info' ? 'info' : color === 'success' ? 'success' : 'default'}
          size={size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium'}
          sx={{
            color: error ? 'error.main' : 'rgba(255, 255, 255, 0.7)',
            padding: '4px',
            '&.Mui-checked': {
              color: error ? 'error.main' : '#64B5F6',
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
          {...props}
        />
        <Box
          sx={{
            flex: 1,
            userSelect: 'none',
          }}
          onClick={(e) => {
            // Prevent label clicks from toggling checkbox
            // But allow links to work normally
            if (e.target.tagName !== 'A' && !e.target.closest('a')) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {label}
        </Box>
      </Box>
      {helperText && (
        <FormHelperText
          error={error}
          sx={{
            color: error ? 'error.main' : 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.75rem',
            mt: 0, // Minimal gap below checkbox/label
            mb: 0, // No bottom margin
            ml: 0, // Align with left edge (same as checkbox container)
            lineHeight: 1.2, // Tighter line height for compact spacing
            minHeight: 'auto', // Remove default min-height
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </>
  );
};

AppCheckboxComponent.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']),
  size: PropTypes.oneOf(['small', 'medium']),
};

AppCheckboxComponent.defaultProps = {
  checked: false,
  error: false,
  helperText: '',
  disabled: false,
  color: 'primary',
  size: 'medium'
};

export default AppCheckboxComponent;
